import { adminConsoleConfigured, verifyAdminPassword } from '../admin/password';
import type { Accounts } from '../storage/accounts';
import { isRegistrationOpen, setRegistrationOpen } from '../storage/settings';
import { promptUser, showInfo } from './prompt';

function byId<T extends HTMLElement>(id: string): T {
  const element = document.getElementById(id);
  if (!element) throw new Error(`Élément #${id} introuvable`);
  return element as T;
}

export interface AdminOptions {
  accounts: Accounts;
}

/** Console d'administration locale (enseignant). Renvoie la fonction d'ouverture. */
export function setupAdmin(options: AdminOptions): () => void {
  const overlay = byId<HTMLDivElement>('admin-overlay');
  const loginPanel = byId<HTMLDivElement>('admin-login');
  const panel = byId<HTMLDivElement>('admin-panel');
  const passwordInput = byId<HTMLInputElement>('admin-password');
  const error = byId<HTMLParagraphElement>('admin-error');
  const summary = byId<HTMLParagraphElement>('admin-summary');
  const usage = byId<HTMLParagraphElement>('admin-usage');
  const list = byId<HTMLDivElement>('admin-accounts');
  const registration = byId<HTMLInputElement>('admin-registration');

  const close = (): void => {
    overlay.hidden = true;
    passwordInput.value = '';
    error.hidden = true;
  };

  const renderPanel = async (): Promise<void> => {
    loginPanel.hidden = true;
    panel.hidden = false;

    const accounts = options.accounts.list();
    summary.textContent = `${accounts.length} compte(s) dans ce navigateur.`;

    usage.textContent = '';
    try {
      const estimate = await navigator.storage?.estimate?.();
      if (estimate?.usage)
        usage.textContent = `Espace utilisé : ${(estimate.usage / 1024).toFixed(1)} Ko.`;
    } catch {
      /* estimation indisponible */
    }

    registration.checked = isRegistrationOpen();
    list.replaceChildren();
    if (accounts.length === 0) {
      const empty = document.createElement('p');
      empty.textContent = 'Aucun compte sur ce navigateur.';
      list.appendChild(empty);
      return;
    }
    for (const account of accounts) {
      const row = document.createElement('label');
      row.className = 'admin-account';
      const checkbox = document.createElement('input');
      checkbox.type = 'checkbox';
      checkbox.value = account.id;
      checkbox.dataset.adminAccount = '1';
      const name = document.createElement('span');
      name.className = 'admin-account-name';
      name.textContent = account.id;
      const date = document.createElement('span');
      date.className = 'admin-date';
      date.textContent = new Date(account.lastUsedAt).toLocaleDateString('fr-FR');
      row.append(checkbox, name, date);
      list.appendChild(row);
    }
  };

  const open = (): void => {
    overlay.hidden = false;
    loginPanel.hidden = true;
    panel.hidden = true;
    error.hidden = true;

    if (!adminConsoleConfigured()) {
      error.textContent =
        'Console désactivée : ADMIN_PASSWORD n’a pas été défini lors du build du site.';
      error.hidden = false;
      return;
    }
    loginPanel.hidden = false;
    passwordInput.value = '';
    passwordInput.focus();
  };

  const tryLogin = async (): Promise<void> => {
    if (await verifyAdminPassword(passwordInput.value)) {
      await renderPanel();
    } else {
      error.textContent = 'Mot de passe incorrect.';
      error.hidden = false;
    }
  };

  byId('admin-submit').addEventListener('click', () => void tryLogin());
  passwordInput.addEventListener('keydown', (event) => {
    if (event.key === 'Enter') void tryLogin();
  });
  byId('admin-close').addEventListener('click', close);
  byId('admin-close-2').addEventListener('click', close);

  registration.addEventListener('change', () => setRegistrationOpen(registration.checked));

  byId('admin-delete-selected').addEventListener('click', () => {
    void (async () => {
      const ids = [
        ...list.querySelectorAll<HTMLInputElement>('input[data-admin-account]:checked'),
      ].map((input) => input.value);
      if (ids.length === 0) {
        await showInfo('Administration', 'Aucun compte sélectionné.');
        return;
      }
      const confirmed = await promptUser({
        title: 'Supprimer des comptes',
        message: `Suppression définitive (aucune récupération possible) : ${ids.join(', ')} ?`,
        submitLabel: 'Supprimer',
      });
      if (!confirmed) return;
      for (const id of ids) await options.accounts.remove(id);
      await renderPanel();
    })();
  });

  byId('admin-delete-all').addEventListener('click', () => {
    void (async () => {
      const answer = await promptUser({
        title: 'Tout effacer',
        message:
          'Tous les comptes de ce navigateur seront supprimés. Saisissez « TOUT EFFACER » pour confirmer.',
        fields: [{ id: 'confirm', label: 'Confirmation' }],
        submitLabel: 'Tout effacer',
      });
      if (!answer || answer.confirm !== 'TOUT EFFACER') return;
      for (const account of options.accounts.list()) await options.accounts.remove(account.id);
      await renderPanel();
    })();
  });

  return open;
}
